import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { BarChart3, Clock, User, Car, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addWeeks, subWeeks, isSameDay, isToday } from 'date-fns';
import AppointmentStatusUpdateContainer from './AppointmentStatusUpdateContainer';

const GanttContainer = styled.div`
  background: #25253C;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    border-radius: 8px;
  }
`;

const GanttHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const WeekTitle = styled.h2`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #2A2A2A;
  background: transparent;
  color: #9E9E9E;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #F43F60;
    color: #F43F60;
    background: rgba(244, 63, 96, 0.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const TimelineHeader = styled.div`
  display: grid;
  grid-template-columns: 200px repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 16px;
  min-width: 800px;
  
  @media (max-width: 768px) {
    grid-template-columns: 150px repeat(7, 1fr);
    gap: 6px;
    margin-bottom: 12px;
    min-width: 600px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 100px repeat(7, 1fr);
    gap: 2px;
    margin-bottom: 8px;
    min-width: 400px;
  }
`;

const TimelineDay = styled.div`
  color: #9E9E9E;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  padding: 8px 4px;
  border-bottom: 1px solid #2A2A2A;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 6px 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 2px 1px;
  }
`;

const AppointmentLabel = styled.div`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 8px 4px;
  border-bottom: 1px solid #2A2A2A;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 6px 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 2px 1px;
  }
`;

const GanttGrid = styled.div`
  display: grid;
  grid-template-columns: 200px repeat(7, 1fr);
  gap: 8px;
  min-width: 800px;
  
  @media (max-width: 768px) {
    grid-template-columns: 150px repeat(7, 1fr);
    gap: 6px;
    min-width: 600px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 100px repeat(7, 1fr);
    gap: 2px;
    min-width: 400px;
  }
`;

const AppointmentRow = styled.div`
  display: contents;
`;

const AppointmentInfo = styled.div`
  background: #1E1E35;
  border: 1px solid #1E1E35;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    padding: 8px;
    margin-bottom: 6px;
    border-radius: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 4px;
    margin-bottom: 4px;
    border-radius: 3px;
  }
`;

const AppointmentTitle = styled.div`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    margin-bottom: 3px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 2px;
  }
`;

const AppointmentDetails = styled.div`
  color: #fff;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    width: 12px;
    height: 12px;
  }
  
  @media (max-width: 768px) {
    gap: 3px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
  
  @media (max-width: 480px) {
    gap: 1px;
    
    svg {
      width: 8px;
      height: 8px;
    }
  }
`;

const TimelineCell = styled.div`
  background: #1E1E35;
  border: 1px solid #1E1E35;
  border-radius: 6px;
  padding: 8px;
  min-height: 80px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 6px;
    min-height: 70px;
    border-radius: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 2px;
    min-height: 50px;
    border-radius: 3px;
  }
`;

const AppointmentBar = styled.div`
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 0.8)';
      case 'cancelled': return 'rgba(239, 68, 68, 0.8)';
      default: return 'rgba(244, 63, 96, 0.8)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'completed': return 'rgba(34, 197, 94, 1)';
      case 'cancelled': return 'rgba(239, 68, 68, 1)';
      default: return 'rgba(244, 63, 96, 1)';
    }
  }};
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    background: ${props => {
      switch (props.status) {
        case 'completed': return 'rgba(34, 197, 94, 0.9)';
        case 'cancelled': return 'rgba(239, 68, 68, 0.9)';
        default: return 'rgba(244, 63, 96, 0.9)';
      }
    }};
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 4px 6px;
    margin-bottom: 3px;
    border-radius: 3px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 3px;
    margin-bottom: 1px;
    border-radius: 2px;
  }
`;

const BarTitle = styled.div`
  color: #fff;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
`;

const BarTime = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  
`;

const StatusIndicator = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#22C55E';
      case 'cancelled': return '#EF4444';
      default: return '#F43F60';
    }
  }};
  
  @media (max-width: 480px) {
    width: 4px;
    height: 4px;
    top: 1px;
    right: 1px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #9E9E9E;
  padding: 40px 20px;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
  
  @media (max-width: 768px) {
    padding: 32px 16px;
    
    svg {
      width: 40px;
      height: 40px;
      margin-bottom: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 24px 12px;
    
    svg {
      width: 32px;
      height: 32px;
      margin-bottom: 8px;
    }
  }
`;

const AppointmentsGantt = ({ appointments = [], onAppointmentClick }) => {
    const [currentWeek, setCurrentWeek] = useState(new Date());

    const weekDays = useMemo(() => {
        const start = startOfWeek(currentWeek, { weekStartsOn: 0 });
        const end = endOfWeek(currentWeek, { weekStartsOn: 0 });
        return eachDayOfInterval({ start, end });
    }, [currentWeek]);

    const appointmentsByDate = useMemo(() => {
        const grouped = {};
        appointments.forEach(appointment => {
            const dateKey = format(new Date(appointment.date), 'yyyy-MM-dd');
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(appointment);
        });
        return grouped;
    }, [appointments]);

    const handlePreviousWeek = () => {
        setCurrentWeek(subWeeks(currentWeek, 1));
    };

    const handleNextWeek = () => {
        setCurrentWeek(addWeeks(currentWeek, 1));
    };

    const handleAppointmentClick = (appointment, e) => {
        e.stopPropagation();
        if (onAppointmentClick) {
            onAppointmentClick(appointment);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#22C55E';
            case 'cancelled': return '#EF4444';
            default: return '#F43F60';
        }
    };

    if (appointments.length === 0) {
        return (
            <EmptyState>
                <BarChart3 />
                <div>No appointments found for the selected date range</div>
            </EmptyState>
        );
    }

    return (
        <GanttContainer>
            <GanttHeader>
                <WeekTitle>
                    {format(weekDays[0], 'MMM d')} - {format(weekDays[6], 'MMM d, yyyy')}
                </WeekTitle>
                <NavigationButtons>
                    <NavButton onClick={handlePreviousWeek} title="Previous Week">
                        <ChevronLeft />
                    </NavButton>
                    <NavButton onClick={handleNextWeek} title="Next Week">
                        <ChevronRight />
                    </NavButton>
                </NavigationButtons>
            </GanttHeader>
            
            <TimelineHeader>
                <div></div>
                {weekDays.map((day, index) => (
                    <TimelineDay key={index}>
                        <div>{format(day, 'EEE')}</div>
                        <div style={{ 
                            color: isToday(day) ? '#F43F60' : '#9E9E9E',
                            fontWeight: isToday(day) ? '700' : '400'
                        }}>
                            {format(day, 'd')}
                        </div>
                    </TimelineDay>
                ))}
            </TimelineHeader>
            
            <GanttGrid>
                {appointments.map((appointment, index) => (
                    <AppointmentRow key={appointment.id || index}>
                        <AppointmentInfo>
                            <AppointmentTitle>
                                {appointment.offer?.vehicle?.year} {appointment.offer?.vehicle?.make} {appointment.offer?.vehicle?.model}
                            </AppointmentTitle>
                            <AppointmentDetails>
                                <DetailItem>
                                    <User size={12} />
                                    {appointment.offer?.user?.userInfo?.name || '--'}
                                </DetailItem>
                                <DetailItem>
                                    <Clock size={12} />
                                    {appointment.timeSlot?.start || '--'} - {appointment.timeSlot?.end || '--'}
                                </DetailItem>
                                <DetailItem>
                                    <Calendar size={12} />
                                    {format(new Date(appointment.date), 'MMM d, yyyy')}
                                </DetailItem>
                            </AppointmentDetails>
                        </AppointmentInfo>
                        
                        {weekDays.map((day, dayIndex) => {
                            const dateKey = format(day, 'yyyy-MM-dd');
                            const appointmentDate = format(new Date(appointment.date), 'yyyy-MM-dd');
                            const isAppointmentDay = dateKey === appointmentDate;
                            
                            return (
                                <TimelineCell key={dayIndex}>
                                    {isAppointmentDay && (
                                        <AppointmentBar 
                                            status={appointment.status}
                                            onClick={(e) => handleAppointmentClick(appointment, e)}
                                            title="Click to view details"
                                        >
                                            <StatusIndicator status={appointment.status} />
                                            <BarTitle>
                                                {appointment.offer?.vehicle?.year} {appointment.offer?.vehicle?.make}
                                            </BarTitle>
                                            <BarTime>
                                                <Clock size={10} />
                                                {appointment.timeSlot?.start || '--'}
                                            </BarTime>
                                        </AppointmentBar>
                                    )}
                                </TimelineCell>
                            );
                        })}
                    </AppointmentRow>
                ))}
            </GanttGrid>
        </GanttContainer>
    );
};

export default AppointmentsGantt; 